<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117] flex flex-col">

    <!-- ── Sticky header ── -->
    <div class="sticky top-14 md:top-0 z-10 flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3 px-5 py-3.5">

        <!-- Title + count -->
        <div class="flex-shrink-0">
          <div class="flex items-center gap-2">
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">Admin Users</h1>
            <span v-if="page.total_records > 0"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 tabular-nums">
              {{ page.total_records.toLocaleString() }}
            </span>
          </div>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Page {{ page.current_page }} of {{ page.total_pages }}
          </p>
        </div>

        <div class="flex-1"></div>

        <!-- Sort by -->
        <div class="relative hidden sm:block">
          <select v-model="sortBy" @change="resetAndFetch"
            class="pl-3 pr-8 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer appearance-none">
            <option value="created_at">Created at</option>
            <option value="updated_at">Updated at</option>
            <option value="events_count">Events</option>
            <option value="bookings_count">Bookings</option>
            <option value="connected_calendars_count">Calendars</option>
          </select>
          <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
          </svg>
        </div>

        <!-- Sort order -->
        <div class="relative hidden sm:block">
          <select v-model="sortOrder" @change="resetAndFetch"
            class="pl-3 pr-8 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer appearance-none">
            <option value="DESC">Desc</option>
            <option value="ASC">Asc</option>
          </select>
          <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
          </svg>
        </div>

        <!-- Per page -->
        <div class="relative hidden sm:block">
          <select v-model="limit" @change="resetAndFetch"
            class="pl-3 pr-8 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer appearance-none">
            <option v-for="n in [10,20,50,100,500]" :key="n" :value="n">{{ n }} / page</option>
          </select>
          <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
          </svg>
        </div>

        <!-- Search -->
        <div class="relative w-48 sm:w-64">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" type="search" placeholder="Search users..."
            class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"/>
        </div>

        <!-- Mobile filter toggle -->
        <button @click="showFilters = !showFilters"
          class="sm:hidden flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          :class="{ 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400': activeFilterCount > 0 }">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"/>
          </svg>
          Filters
          <span v-if="activeFilterCount > 0" class="ml-0.5 px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">{{ activeFilterCount }}</span>
        </button>

      </div>
    </div>

    <!-- ── Body ── -->
    <div class="flex flex-1 overflow-hidden">

      <!-- ── Filter sidebar ── -->
      <Transition name="filter-slide">
        <div v-show="showFilters || isDesktop"
          class="w-full sm:w-64 flex-shrink-0 bg-white dark:bg-[#1C2128] border-r border-gray-200 dark:border-gray-700 overflow-y-auto absolute sm:relative inset-0 z-20 sm:z-auto sm:top-auto"
          style="sm:height: calc(100vh - 56px)">

          <div class="p-5 space-y-5">
            <div class="flex items-center justify-between sm:hidden">
              <p class="text-sm font-bold text-gray-900 dark:text-white">Filters</p>
              <button @click="showFilters = false" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Active filter count -->
            <div v-if="activeFilterCount > 0" class="flex items-center justify-between">
              <span class="text-xs font-semibold text-blue-600 dark:text-blue-400">{{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} active</span>
              <button @click="clearFilters" class="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Clear all</button>
            </div>

            <!-- Boolean filters -->
            <div class="space-y-1.5">
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Filter by</p>
              <label v-for="filter in booleanFilters" :key="filter.key"
                class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60 group">
                <div class="relative flex-shrink-0">
                  <input type="checkbox" v-model="filter.value" class="sr-only"/>
                  <div class="w-4 h-4 rounded border-2 transition-colors flex items-center justify-center"
                    :class="filter.value
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 group-hover:border-gray-400'">
                    <svg v-if="filter.value" class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <span class="text-sm transition-colors"
                  :class="filter.value
                    ? 'text-blue-700 dark:text-blue-400 font-semibold'
                    : 'text-gray-700 dark:text-gray-300'">
                  {{ filter.label }}
                </span>
              </label>
            </div>

            <div class="border-t border-gray-100 dark:border-gray-700/60"></div>

            <!-- Select filters -->
            <div class="space-y-4">
              <div class="space-y-1.5">
                <label class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Test Users</label>
                <div class="relative">
                  <select v-model="testUsers"
                    class="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer">
                    <option v-for="item in ['all','true','false']" :key="item" :value="item">
                      {{ item.charAt(0).toUpperCase() + item.slice(1) }}
                    </option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                  </svg>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Provider</label>
                <div class="relative">
                  <select v-model="provider"
                    class="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer">
                    <option v-for="p in ['All','manual','google']" :key="p" :value="p">
                      {{ p.charAt(0).toUpperCase() + p.slice(1) }}
                    </option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                  </svg>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Locale</label>
                <div class="relative">
                  <select v-model="locale"
                    class="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer">
                    <option v-for="opt in localeOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                  </svg>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Timezone</label>
                <div class="relative">
                  <select v-model="timezone"
                    class="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer">
                    <option :value="null">All</option>
                    <option v-for="tz in ['Karachi','Germany','Maldives','England']" :key="tz" :value="tz">{{ tz }}</option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                  </svg>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Currency</label>
                <div class="relative">
                  <select v-model="currency"
                    class="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer">
                    <option :value="null">All</option>
                    <option v-for="c in ['eur','pkr','usd','inr']" :key="c" :value="c">{{ c.toUpperCase() }}</option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- ── Main content ── -->
      <div class="flex-1 min-w-0 overflow-y-auto px-5 py-5">

        <!-- Loading skeleton -->
        <template v-if="loading">
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
            <div class="px-6 py-3 bg-gray-50 dark:bg-gray-800/40 flex gap-6">
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
            <div v-for="i in limit" :key="i" class="flex items-center gap-4 px-6 py-4 border-t border-gray-50 dark:border-gray-800">
              <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-1.5">
                <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
                <div class="flex gap-1.5">
                  <div class="h-4 w-14 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                  <div class="h-4 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                </div>
              </div>
              <div class="hidden md:block space-y-1.5 w-28">
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-3/4"></div>
              </div>
              <div class="hidden lg:flex gap-2 w-36">
                <div class="h-5 w-10 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                <div class="h-5 w-10 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                <div class="h-5 w-12 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
              </div>
              <div class="flex gap-2">
                <div class="h-7 w-12 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                <div class="h-7 w-20 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <template v-else-if="users.length === 0">
          <div class="flex flex-col items-center justify-center py-32 text-center">
            <div class="w-20 h-20 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-5">
              <svg class="w-10 h-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"/>
              </svg>
            </div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-2">No users found</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs">Try adjusting your filters or search term.</p>
            <button @click="clearFilters" class="mt-5 px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
              Clear all filters
            </button>
          </div>
        </template>

        <!-- Users table -->
        <template v-else>
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

            <!-- Table header -->
            <div class="grid grid-cols-[1fr_auto_auto_auto] items-center px-6 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-700 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <span>User</span>
              <span class="hidden md:block w-32">Location</span>
              <span class="hidden lg:block w-36 text-center">Stats</span>
              <span class="w-36 text-right">Actions</span>
            </div>

            <!-- User rows -->
            <div v-for="user in users" :key="user.id"
              class="grid grid-cols-[1fr_auto_auto_auto] items-center px-6 py-4 border-t border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors"
              :class="{ 'opacity-50': user.deleted }">

              <!-- User info -->
              <div class="flex items-start gap-3 min-w-0">
                <!-- Avatar -->
                <div class="flex-shrink-0 mt-0.5">
                  <img v-if="user.profile_picture_url" :src="user.profile_picture_url" :alt="user.display_name"
                    class="w-9 h-9 rounded-full object-cover"/>
                  <div v-else
                    class="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    :style="{ backgroundColor: avatarColor(user.email) }">
                    {{ user.avatar_name }}
                  </div>
                </div>

                <!-- Name / email / badges -->
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <!-- Email confirmed -->
                    <svg v-if="user.is_email_confirmed" class="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 512 512">
                      <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z"/>
                    </svg>
                    <svg v-else class="w-3.5 h-3.5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 512 512">
                      <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z"/>
                    </svg>

                    <!-- Name -->
                    <a :href="`${APPLICATION_URL}/${user.url}`" target="_blank"
                      class="text-sm font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate leading-tight">
                      {{ user.display_name }}
                    </a>

                    <!-- Super admin crown -->
                    <svg v-if="user.is_super_admin" class="w-4 h-4 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3a1 1 0 0 1-1 1H6a1 1 0 0 1 0-2h12a1 1 0 0 1 1 1z"/>
                    </svg>
                  </div>

                  <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ user.email }}</p>

                  <!-- Badges -->
                  <div class="flex items-center flex-wrap gap-1 mt-1.5">
                    <!-- Provider -->
                    <span class="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      :class="user.provider === 'google'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
                      {{ user.provider.charAt(0).toUpperCase() + user.provider.slice(1) }}
                    </span>
                    <!-- Onboarded -->
                    <span v-if="user.onboarding_submitted_at"
                      class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                      Onboarded
                    </span>
                    <!-- Test user -->
                    <span v-if="user.test_user"
                      class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400">
                      Test
                    </span>
                    <!-- Deleted -->
                    <span v-if="user.deleted"
                      class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400"
                      :title="user.deleted_reason">
                      Deleted
                    </span>
                  </div>
                </div>
              </div>

              <!-- Location + date -->
              <div class="hidden md:block w-32 pl-4">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{{ user.signup_location || '—' }}</p>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{{ user.created_at_for_ui }}</p>
              </div>

              <!-- Stats -->
              <div class="hidden lg:flex items-center gap-1.5 w-36 justify-center flex-wrap">
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 tabular-nums" title="Events">
                  <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Z" clip-rule="evenodd"/></svg>
                  {{ user.events_count }}
                </span>
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 tabular-nums" title="Bookings">
                  <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clip-rule="evenodd"/></svg>
                  {{ user.bookings_count }}
                </span>
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 tabular-nums" title="Connected Calendars">
                  <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 0 0 .977-1.138 2.5 2.5 0 0 1-.142-3.667l3-3Z"/><path d="M11.603 7.963a.75.75 0 0 0-.977 1.138 2.5 2.5 0 0 1 .142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865Z"/></svg>
                  {{ user.connected_calendars_count }}
                </span>
              </div>

              <!-- Actions -->
              <div class="w-36 flex items-center justify-end gap-1.5 flex-shrink-0">
                <!-- Login as -->
                <a :href="user.login_as_user_link"
                  class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15"/>
                  </svg>
                  Login
                </a>

                <!-- Test user toggle -->
                <button @click="testUser(user)"
                  class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  :class="user.test_user
                    ? 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40'
                    : 'text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  {{ user.test_user ? 'Untest' : 'Test' }}
                </button>

                <!-- Delete -->
                <button v-if="user.is_destroy_able" @click="deleteUser(user)"
                  class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                  </svg>
                </button>
              </div>

            </div>
          </div>

          <!-- Pagination -->
          <div class="flex items-center justify-between mt-4">
            <p class="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {{ page.total_records.toLocaleString() }} users
              <span class="text-gray-400 dark:text-gray-600 mx-1">·</span>
              Page {{ page.current_page }} of {{ page.total_pages }}
            </p>
            <div class="flex items-center gap-2">
              <button @click="prevPage" :disabled="page.current_page === 1"
                class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :class="page.current_page === 1
                  ? 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600'
                  : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
                </svg>
                Prev
              </button>
              <button @click="nextPage" :disabled="page.current_page === page.total_pages"
                class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :class="page.current_page === page.total_pages
                  ? 'bg-blue-300 dark:bg-blue-900/50 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'">
                Next
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N, APPLICATION_URL } = useConstants()

// ── Layout ──
const showFilters = ref(false)
const isDesktop = ref(window.innerWidth >= 640)

function handleResize() { isDesktop.value = window.innerWidth >= 640 }
onMounted(() => window.addEventListener('resize', handleResize))
onUnmounted(() => window.removeEventListener('resize', handleResize))

// ── State ──
const loading = ref(true)
const users = ref([])
const page = reactive({ current_page: 1, total_pages: 1, total_records: 0, limit: 10, number: 1 })

// Sort / search
const sortBy    = ref('created_at')
const sortOrder = ref('DESC')
const limit     = ref(10)
const search    = ref('')
let searchTimer = null

// Boolean filters
const emailConfirmed         = ref(false)
const createdByAdmin         = ref(false)
const logDetailedAhoyEvents  = ref(false)
const deleted                = ref(false)
const onboardingSubmitted    = ref(false)
const superAdmin             = ref(false)
const withCCalendar          = ref(false)

// Select filters
const testUsers = ref('false')
const provider  = ref('All')
const timezone  = ref(null)
const locale    = ref('all')
const currency  = ref(null)

// ── Boolean filter array (for clean template) ──
const booleanFilters = [
  { key: 'emailConfirmed',        label: 'Email Confirmed',     get value() { return emailConfirmed.value        }, set value(v) { emailConfirmed.value = v        } },
  { key: 'createdByAdmin',        label: 'Created by admin',    get value() { return createdByAdmin.value        }, set value(v) { createdByAdmin.value = v        } },
  { key: 'logDetailedAhoyEvents', label: 'Ahoy events',         get value() { return logDetailedAhoyEvents.value }, set value(v) { logDetailedAhoyEvents.value = v } },
  { key: 'deleted',               label: 'Deleted users',       get value() { return deleted.value               }, set value(v) { deleted.value = v               } },
  { key: 'onboardingSubmitted',   label: 'Onboarding submitted',get value() { return onboardingSubmitted.value   }, set value(v) { onboardingSubmitted.value = v   } },
  { key: 'superAdmin',            label: 'Super admins',        get value() { return superAdmin.value            }, set value(v) { superAdmin.value = v            } },
  { key: 'withCCalendar',         label: 'With C-Calendar',     get value() { return withCCalendar.value         }, set value(v) { withCCalendar.value = v         } },
]

// ── Active filter count ──
const activeFilterCount = computed(() => {
  let n = 0
  if (emailConfirmed.value)        n++
  if (createdByAdmin.value)        n++
  if (logDetailedAhoyEvents.value) n++
  if (deleted.value)               n++
  if (onboardingSubmitted.value)   n++
  if (superAdmin.value)            n++
  if (withCCalendar.value)         n++
  if (testUsers.value !== 'false') n++
  if (provider.value !== 'All')    n++
  if (locale.value !== 'all')      n++
  if (timezone.value !== null)     n++
  if (currency.value !== null)     n++
  if (search.value)                n++
  return n
})

function clearFilters() {
  emailConfirmed.value = false
  createdByAdmin.value = false
  logDetailedAhoyEvents.value = false
  deleted.value = false
  onboardingSubmitted.value = false
  superAdmin.value = false
  withCCalendar.value = false
  testUsers.value = 'false'
  provider.value = 'All'
  locale.value = 'all'
  timezone.value = null
  currency.value = null
  search.value = ''
}

// ── Locale options ──
const localeOptions = [
  { value: 'all', text: 'All' },
  { value: 'en',  text: 'English' },
  { value: 'de',  text: 'German' },
  { value: 'es',  text: 'Spanish' },
  { value: 'ja',  text: 'Japanese' },
  { value: 'zh',  text: 'Chinese' },
  { value: 'it',  text: 'Italian' },
  { value: 'ru',  text: 'Russian' },
  { value: 'nl',  text: 'Dutch' },
  { value: 'ar',  text: 'Arabic' },
  { value: 'hi',  text: 'Hindi' },
  { value: 'fr',  text: 'French' },
]

// ── Filters payload ──
const filtersPayload = computed(() => {
  const data = {
    sort_by: sortBy.value,
    sort_order: sortOrder.value,
    page: { limit: limit.value, number: page.number },
    filters: {},
  }
  if (search.value)                  data.search = search.value
  if (emailConfirmed.value)          data.filters.email_confirmed = true
  if (createdByAdmin.value)          data.filters.created_by_admin = true
  if (logDetailedAhoyEvents.value)   data.filters.log_detailed_ahoy_devents = true
  if (deleted.value)                 data.filters.deleted = true
  if (onboardingSubmitted.value)     data.filters.onboarding_submitted = true
  if (superAdmin.value)              data.filters.super_admin = true
  if (withCCalendar.value)           data.filters.connected_calendars_available = true
  if (testUsers.value !== 'all')     data.filters.test_users = testUsers.value
  if (provider.value !== 'All')      data.filters.by_value = { provider: provider.value }
  if (locale.value !== 'all')        data.filters.by_value = { locale: locale.value }
  return data
})

// ── Fetch ──
async function fetchUsersList() {
  loading.value = true
  const res = await axiosRequest('POST', '/api/v1/admin/users', filtersPayload.value, false)
  loading.value = false
  if (!res.js_error && res.response.success) {
    Object.assign(page, res.response.data.page)
    users.value = res.response.data.records
  }
}

function resetAndFetch() {
  page.number = 1
  fetchUsersList()
}

function onSearchInput() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    page.number = 1
    fetchUsersList()
  }, 300)
}

// ── Pagination ──
async function nextPage() {
  if (page.current_page < page.total_pages) {
    page.number++
    await fetchUsersList()
  }
}
async function prevPage() {
  if (page.current_page > 1) {
    page.number--
    await fetchUsersList()
  }
}

// ── Actions ──
async function testUser(user) {
  const res = await axiosRequest('PUT', `/api/v1/admin/users/${user.id}`,
    { user: { mark_as_test_user: !user.test_user } }, false)
  if (!res.js_error && res.response.success) {
    flash.value?.show(user.test_user ? 'Removed from Test Users' : 'Added into Test Users', 'notice')
    await fetchUsersList()
  }
}

async function deleteUser(user) {
  const res = await axiosRequest('DELETE', `/api/v1/admin/users/${user.id}`, {}, false)
  if (!res.js_error && res.response.success) {
    flash.value?.show('User deleted', 'notice')
    await fetchUsersList()
  }
}

// ── Avatar color ──
const AVATAR_COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#be185d']
function avatarColor(email) {
  let hash = 0
  for (let i = 0; i < (email || '').length; i++) hash = (email || '').charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

// ── Watch filters (re-fetch on change, reset page) ──
watch([emailConfirmed, createdByAdmin, logDetailedAhoyEvents, deleted,
       onboardingSubmitted, superAdmin, withCCalendar,
       testUsers, provider, timezone, locale, currency,
       sortBy, sortOrder], () => {
  page.number = 1
  fetchUsersList()
})

watch(limit, () => resetAndFetch())

onMounted(fetchUsersList)
</script>

<style scoped>
.filter-slide-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.filter-slide-leave-active { transition: opacity 0.15s ease; }
.filter-slide-enter-from   { opacity: 0; transform: translateX(-8px); }
.filter-slide-leave-to     { opacity: 0; }
</style>
